import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Banknote, CreditCard, ArrowRight, Check, Tag, X, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const { cart, clearCart } = useCart();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [paymentMethod, setPaymentMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  
  // Gift Card state
  const [giftCardCode, setGiftCardCode] = useState('');
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);
  const [applyingGiftCard, setApplyingGiftCard] = useState(false);
  
  const [formData, setFormData] = useState({
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    notes: ''
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const subtotal = cart.items ? cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0) : 0;
  const couponDiscount = appliedCoupon ? appliedCoupon.discount_amount : 0;
  const giftCardDiscount = appliedGiftCard ? Math.min(appliedGiftCard.remaining_value, subtotal - couponDiscount) : 0;
  const discount = couponDiscount + giftCardDiscount;
  const total = Math.max(0, subtotal - discount);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setApplyingCoupon(true);
    try {
      const res = await axios.post(`${API}/coupons/validate?code=${couponCode}&subtotal=${subtotal}`);
      setAppliedCoupon(res.data);
      toast.success(`Cupom ${res.data.code} aplicado! -$${res.data.discount_amount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cupom inválido');
      setAppliedCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Cupom removido');
  };
  
  // Gift Card handlers
  const handleApplyGiftCard = async () => {
    if (!giftCardCode.trim()) return;
    
    setApplyingGiftCard(true);
    try {
      const res = await axios.post(`${API}/gift-cards/validate`, { code: giftCardCode });
      setAppliedGiftCard(res.data);
      toast.success(`Vale presente aplicado! Saldo: $${res.data.remaining_value.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Vale presente inválido');
      setAppliedGiftCard(null);
    } finally {
      setApplyingGiftCard(false);
    }
  };

  const handleRemoveGiftCard = () => {
    setAppliedGiftCard(null);
    setGiftCardCode('');
    toast.info('Vale presente removido');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentMethod) {
      toast.error('Selecione um método de pagamento');
      return;
    }

    if (!formData.street || !formData.city || !formData.state || !formData.zip_code) {
      toast.error('Preencha todos os campos de endereço');
      return;
    }

    setLoading(true);
    try {
      // Create order first
      const response = await axios.post(`${API}/orders`, {
        payment_method: paymentMethod,
        shipping_address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          country: formData.country
        },
        notes: formData.notes || null,
        coupon_code: appliedCoupon?.code || null,
        discount_amount: appliedCoupon?.discount_amount || 0
      });

      const orderId = response.data.id;

      // If card payment, redirect to Stripe
      if (paymentMethod === 'card') {
        try {
          const stripeResponse = await axios.post(`${API}/checkout/stripe`, {
            order_id: orderId,
            origin_url: window.location.origin
          });
          
          // Redirect to Stripe Checkout
          window.location.href = stripeResponse.data.url;
          return;
        } catch (stripeError) {
          toast.error('Erro ao iniciar pagamento com cartão');
          setLoading(false);
          return;
        }
      }

      // If Afterpay payment, redirect to Afterpay via Stripe
      if (paymentMethod === 'afterpay') {
        try {
          const afterpayResponse = await axios.post(`${API}/checkout/afterpay`, {
            order_id: orderId,
            origin_url: window.location.origin
          });
          
          // Redirect to Afterpay Checkout
          window.location.href = afterpayResponse.data.url;
          return;
        } catch (afterpayError) {
          const errorMsg = afterpayError.response?.data?.detail || 'Erro ao iniciar pagamento Afterpay';
          toast.error(errorMsg);
          setLoading(false);
          return;
        }
      }

      // For cash/zelle, go to confirmation page
      toast.success('Pedido realizado com sucesso!');
      navigate(`/order-confirmation/${orderId}`);
    } catch (error) {
      toast.error('Erro ao processar pedido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (!cart.items || cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen pt-24" data-testid="checkout-page">
      <div className="container-luxury py-12">
        <h1 className="font-serif text-4xl mb-12">Finalizar Compra</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Column - Form */}
            <div className="lg:col-span-2 space-y-12">
              {/* Shipping Address */}
              <section>
                <h2 className="font-serif text-2xl mb-6">Endereço de Entrega</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2 form-group">
                    <Label className="form-label">Endereço</Label>
                    <Input
                      type="text"
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                      placeholder="Rua, número, complemento"
                      className="input-luxury"
                      required
                      data-testid="address-street"
                    />
                  </div>
                  <div className="form-group">
                    <Label className="form-label">Cidade</Label>
                    <Input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Cidade"
                      className="input-luxury"
                      required
                      data-testid="address-city"
                    />
                  </div>
                  <div className="form-group">
                    <Label className="form-label">Estado</Label>
                    <Input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      placeholder="Estado"
                      className="input-luxury"
                      required
                      data-testid="address-state"
                    />
                  </div>
                  <div className="form-group">
                    <Label className="form-label">CEP / Zip Code</Label>
                    <Input
                      type="text"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      placeholder="00000-000"
                      className="input-luxury"
                      required
                      data-testid="address-zipcode"
                    />
                  </div>
                  <div className="form-group">
                    <Label className="form-label">País</Label>
                    <Input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      placeholder="País"
                      className="input-luxury"
                      required
                      data-testid="address-country"
                    />
                  </div>
                </div>
              </section>

              {/* Payment Method */}
              <section>
                <h2 className="font-serif text-2xl mb-6">Método de Pagamento</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Card Payment */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`payment-card text-left ${paymentMethod === 'card' ? 'selected' : ''}`}
                    data-testid="payment-card"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 border flex items-center justify-center ${paymentMethod === 'card' ? 'border-primary' : 'border-white/20'}`}>
                        <CreditCard className={`w-6 h-6 ${paymentMethod === 'card' ? 'text-primary' : ''}`} />
                      </div>
                      <div>
                        <h3 className="font-medium">Cartão</h3>
                        <p className="text-sm text-muted-foreground">Crédito ou Débito</p>
                      </div>
                      {paymentMethod === 'card' && (
                        <Check className="w-5 h-5 text-primary ml-auto" />
                      )}
                    </div>
                  </button>

                  {/* Cash Payment */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('cash')}
                    className={`payment-card text-left ${paymentMethod === 'cash' ? 'selected' : ''}`}
                    data-testid="payment-cash"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 border flex items-center justify-center ${paymentMethod === 'cash' ? 'border-primary' : 'border-white/20'}`}>
                        <Banknote className={`w-6 h-6 ${paymentMethod === 'cash' ? 'text-primary' : ''}`} />
                      </div>
                      <div>
                        <h3 className="font-medium">Dinheiro</h3>
                        <p className="text-sm text-muted-foreground">Pague na entrega</p>
                      </div>
                      {paymentMethod === 'cash' && (
                        <Check className="w-5 h-5 text-primary ml-auto" />
                      )}
                    </div>
                  </button>

                  {/* Zelle Payment */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('zelle')}
                    className={`payment-card text-left ${paymentMethod === 'zelle' ? 'selected' : ''}`}
                    data-testid="payment-zelle"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 border flex items-center justify-center ${paymentMethod === 'zelle' ? 'border-primary' : 'border-white/20'}`}>
                        <Banknote className={`w-6 h-6 ${paymentMethod === 'zelle' ? 'text-primary' : ''}`} />
                      </div>
                      <div>
                        <h3 className="font-medium">Zelle</h3>
                        <p className="text-sm text-muted-foreground">Transferência</p>
                      </div>
                      {paymentMethod === 'zelle' && (
                        <Check className="w-5 h-5 text-primary ml-auto" />
                      )}
                    </div>
                  </button>

                  {/* Afterpay Payment */}
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('afterpay')}
                    className={`payment-card text-left ${paymentMethod === 'afterpay' ? 'selected' : ''}`}
                    data-testid="payment-afterpay"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 border flex items-center justify-center ${paymentMethod === 'afterpay' ? 'border-primary bg-[#b2fce4]/10' : 'border-white/20'}`}>
                        <svg className={`w-6 h-6 ${paymentMethod === 'afterpay' ? 'text-[#b2fce4]' : 'text-muted-foreground'}`} viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-2h2v2zm0-4h-2V7h2v6zm4 4h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-medium">Afterpay</h3>
                        <p className="text-sm text-muted-foreground">Parcele em 4x sem juros</p>
                      </div>
                      {paymentMethod === 'afterpay' && (
                        <Check className="w-5 h-5 text-primary ml-auto" />
                      )}
                    </div>
                  </button>
                </div>

                {paymentMethod === 'afterpay' && (
                  <div className="mt-6 p-6 bg-[#b2fce4]/10 border border-[#b2fce4]/30 rounded-lg">
                    <h4 className="font-medium mb-3 text-[#b2fce4]">Como funciona o Afterpay:</h4>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>✓ Pague em <strong>4 parcelas iguais</strong>, a cada 2 semanas</p>
                      <p>✓ <strong>Sem juros</strong> - você paga apenas o valor do produto</p>
                      <p>✓ Primeira parcela no ato da compra</p>
                      <p>✓ Pedidos entre <strong>$1 e $2,000</strong></p>
                      <p className="text-[#b2fce4] mt-4">
                        💡 Você será redirecionado para o Afterpay para concluir o pagamento
                      </p>
                    </div>
                  </div>
                )}

                {paymentMethod === 'zelle' && (
                  <div className="mt-6 p-6 bg-card border border-primary/30">
                    <h4 className="font-medium mb-4">Informações para pagamento via Zelle:</h4>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p><strong>Email:</strong> {settings.zelle_email || 'Carregando...'}</p>
                      <p><strong>Telefone:</strong> {settings.zelle_phone || 'Carregando...'}</p>
                      <p><strong>Nome:</strong> {settings.zelle_name || 'Carregando...'}</p>
                      <p className="text-primary mt-4">
                        ⓘ Inclua o número do pedido no memo do pagamento
                      </p>
                    </div>
                  </div>
                )}
              </section>

              {/* Notes */}
              <section>
                <h2 className="font-serif text-2xl mb-6">Observações (Opcional)</h2>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Instruções especiais para entrega..."
                  className="input-luxury min-h-[120px] border border-white/20"
                  data-testid="order-notes"
                />
              </section>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="p-8 bg-card border border-white/10 sticky top-28">
                <h2 className="font-serif text-2xl mb-6">Resumo do Pedido</h2>
                
                {/* Items */}
                <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                  {cart.items.map((item) => (
                    <div key={item.id} className="flex gap-4">
                      <div className="w-16 h-20 bg-card flex-shrink-0 overflow-hidden">
                        <img
                          src={item.product_image}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{item.product_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.size} | {item.color} | x{item.quantity}
                        </p>
                        <p className="text-sm text-primary mt-1">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4 mb-6 border-t border-white/10 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  
                  {/* Coupon Section */}
                  {!appliedCoupon ? (
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Código do cupom"
                        className="input-luxury text-sm uppercase"
                        data-testid="coupon-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleApplyCoupon}
                        disabled={applyingCoupon}
                        className="border-white/20 hover:bg-white/5"
                        data-testid="apply-coupon-btn"
                      >
                        {applyingCoupon ? '...' : <Tag className="w-4 h-4" />}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center text-sm bg-green-500/10 p-2 rounded">
                      <span className="text-green-500 flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        {appliedCoupon.code}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-green-500">-${appliedCoupon.discount_amount.toFixed(2)}</span>
                        <button 
                          onClick={handleRemoveCoupon}
                          className="text-muted-foreground hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Gift Card Section */}
                  {!appliedGiftCard ? (
                    <div className="flex gap-2">
                      <Input
                        value={giftCardCode}
                        onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                        placeholder="Vale presente (GIFT-XXXX-XXXX)"
                        className="input-luxury text-sm uppercase"
                        data-testid="gift-card-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleApplyGiftCard}
                        disabled={applyingGiftCard}
                        className="border-white/20 hover:bg-white/5"
                        data-testid="apply-gift-card-btn"
                      >
                        {applyingGiftCard ? '...' : <Gift className="w-4 h-4" />}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center text-sm bg-primary/10 p-2 rounded">
                      <span className="text-primary flex items-center gap-2">
                        <Gift className="w-4 h-4" />
                        {appliedGiftCard.code}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-primary">-${giftCardDiscount.toFixed(2)}</span>
                        <button 
                          onClick={handleRemoveGiftCard}
                          className="text-muted-foreground hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Frete</span>
                    <span className="text-green-500">Grátis</span>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4 mb-8">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-primary" data-testid="checkout-total">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full"
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                      Processando...
                    </span>
                  ) : (
                    <>
                      Confirmar Pedido
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
